import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { RotateCcw, Trash2, Archive } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { k: "ideas", l: "R&D Ideas" },
  { k: "goals", l: "Goals" },
  { k: "benchmarks", l: "Benchmarks" },
  { k: "services", l: "Services" },
];

export default function Archived() {
  const [tab, setTab] = useState("ideas");
  const [items, setItems] = useState([]);
  const latest = useRef("ideas");

  const load = useCallback((which) => {
    latest.current = which;
    api.get(`/${which}`, { params: { archived: true } })
      .then((r) => { if (latest.current === which) setItems(r.data); })
      .catch(() => {});
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const restore = async (id) => { await api.post(`/${tab}/${id}/restore`); load(tab); toast.success("Restored"); };
  const purge = async (id) => { await api.delete(`/${tab}/${id}`, { params: { permanent: true } }); load(tab); toast.success("Permanently deleted"); };
  const title = (it) => it.title || it.name;

  return (
    <div className="space-y-6" data-testid="archived-page">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Archived / Ignored Bank</h1>
        <p className="text-muted-foreground">Recover items or purge them permanently.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setItems([]); setTab(v); }}>
        <TabsList data-testid="archived-tabs">
          {TABS.map((t) => <TabsTrigger key={t.k} value={t.k} data-testid={`archived-tab-${t.k}`}>{t.l}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-300 py-16 text-muted-foreground">
            <Archive size={28} /> Nothing archived here.
          </div>
        )}
        {items.map((it) => (
          <Card key={it.id} className="flex items-center justify-between gap-4 p-4 border-slate-200" data-testid={`archived-item-${it.id}`}>
            <div className="min-w-0">
              <p className="truncate font-medium text-slate-900">{title(it)}</p>
              <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                {it.category && <Badge variant="outline">{it.category}</Badge>}
                {it.rejection_reason && <Badge className="bg-red-100 text-red-700">Reason: {it.rejection_reason}</Badge>}
                {it.brand_target && <span>{it.brand_target}</span>}
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" size="sm" onClick={() => restore(it.id)} data-testid={`restore-${it.id}`}><RotateCcw size={14} className="mr-1" /> Restore</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" data-testid={`purge-${it.id}`}><Trash2 size={14} /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Permanently delete?</AlertDialogTitle>
                    <AlertDialogDescription>This will permanently remove "{title(it)}". This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => purge(it.id)} data-testid={`confirm-purge-${it.id}`}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
